/**
 * 明细表构建器：表头行 + 数据行（数据单元格 expand=down）。
 * 最简单的报表类型，父格默认即可。
 */

import type { ReportIR, CellIR, DatasetIR, DatasourceIR } from '../ir/types.js';
import { coordName } from '../grid/coordinate.js';

export interface DetailInput {
  name?: string;
  datasetName: string;
  fields: Array<{ name: string; label?: string; type: DatasetIR['fields'][number]['type'] }>;
  datasource: DatasourceIR;
  title?: string;
  paper?: ReportIR['paper'];
  /** 是否在最左侧插入序号列（=seq()），序号格左父格指向右侧首个数据格。 */
  serial?: boolean;
}

export function buildDetail(input: DetailInput): ReportIR {
  const { datasetName, fields, datasource, serial } = input;

  // 序号列占第 1 列；数据列顺延；无序号时列号从 1 开始
  const offset = serial ? 1 : 0;
  const columnCount = fields.length + offset;
  const rowCount = 2; // 第1行表头，第2行数据
  const cells: CellIR[] = [];

  // 表头（第1行）
  if (serial) {
    cells.push({
      row: 1,
      col: 1,
      name: coordName(1, 1),
      content: { kind: 'text', value: '序号' },
      style: { align: 'center', bold: true, bgcolor: '#f2f2f2' },
    });
  }
  fields.forEach((f, i) => {
    cells.push({
      row: 1,
      col: i + 1 + offset,
      name: coordName(1, i + 1 + offset),
      content: { kind: 'text', value: f.label ?? f.name },
      style: { align: 'center', bold: true, bgcolor: '#f2f2f2' },
    });
  });

  // 序号数据行（第2行）：左父格设为右侧首个数据格，随其向下扩展
  if (serial) {
    const dataColName = coordName(2, 2); // B2：右侧第一个数据格
    cells.push({
      row: 2,
      col: 1,
      name: coordName(2, 1),
      content: { kind: 'expression', value: '=seq()' },
      left: dataColName,
      style: { align: 'center' },
    });
  }

  // 数据行（第2行），向下扩展；首个数据列的左父格设为 none（不被序号列带动）
  fields.forEach((f, i) => {
    const col = i + 1 + offset;
    cells.push({
      row: 2,
      col,
      name: coordName(2, col),
      content: { kind: 'dataset', dataset: datasetName, field: f.name, aggregate: 'none' },
      expand: 'down',
      left: i === 0 && serial ? 'none' : undefined,
    });
  });

  const dataset: DatasetIR = {
    name: datasetName,
    type: 'sql',
    datasource: datasource.name,
    readFieldRemark: false,
    fields: fields.map((f) => ({ name: f.name, label: f.label ?? f.name, type: f.type })),
  };

  return {
    name: input.name ?? '明细表',
    rowCount,
    columnCount,
    cells,
    datasets: [dataset],
    datasources: [datasource],
    title: input.title ?? 'fileName()',
    paper: input.paper,
  };
}
